/**
 * RecognitionRepository - Database operations for recognitions collection
 * CRITICAL: Uses hierarchical paths - schools/{schoolId}/recognitions/{recognitionId}
 */

import { BaseRepository, where } from '../../discipline/repositories/BaseRepository';
import { getDocs, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { isIssuedRecognition, isRecognitionDefinition } from '../../domain/recognitionDefinition';

export class RecognitionRepository extends BaseRepository {
  constructor() {
    // CRITICAL: Enable hierarchical paths - recognitions stored at schools/{schoolId}/recognitions/{recognitionId}
    super('recognitions', { isHierarchical: true });
  }

  /**
   * Create a new recognition (override to add recognition-specific fields)
   * @param {Object} recognitionData - Recognition data
   * @returns {Promise<Object>} Created recognition with ID
   */
  async create(recognitionData) {
    const recognition = {
      ...recognitionData,
      type: 'recognition',
      issuedAt: serverTimestamp(),
      processedForRules: false,
      triggeredBadges: [],
      triggeredCertificates: [],
      parentNotified: false,
      parentNotifiedAt: null
    };

    return await super.create(recognition);
  }

  /**
   * Get recognitions for a specific student
   * @param {string} studentId - Student ID
   * @param {Object} options - Query options (must include schoolId)
   * @returns {Promise<Array>} Array of recognitions
   */
  async getByStudentId(studentId, options = {}) {
    // Guard: require schoolId for hierarchical collections
    if (!options.schoolId) {
      console.warn('RecognitionRepository.getByStudentId: schoolId required but not provided');
      return [];
    }

    const constraints = [
      where('studentId', '==', studentId)
    ];

    // Filter by value if specified
    if (options.valueId) {
      constraints.push(where('valueId', '==', options.valueId));
    }

    // Pass schoolId for hierarchical routing
    const results = await this.queryDocuments(constraints, options.schoolId);

    // Sort client-side by issuedAt (desc)
    results.sort((a, b) => {
      const timeA = a.issuedAt?.toMillis?.() || 0;
      const timeB = b.issuedAt?.toMillis?.() || 0;
      return timeB - timeA;
    });

    // Apply date filtering client-side
    let filtered = results;
    if (options.startDate) {
      const startTime = options.startDate.getTime();
      filtered = filtered.filter(r => {
        const time = r.issuedAt?.toMillis?.() || 0;
        return time >= startTime;
      });
    }
    if (options.endDate) {
      const endTime = options.endDate.getTime();
      filtered = filtered.filter(r => {
        const time = r.issuedAt?.toMillis?.() || 0;
        return time <= endTime;
      });
    }

    // Apply limit client-side
    if (options.limit) {
      filtered = filtered.slice(0, options.limit);
    }

    return filtered;
  }

  /**
   * Get recognitions for a specific class
   * @param {string} classId - Class ID
   * @param {Object} options - Query options (must include schoolId)
   * @returns {Promise<Array>} Array of recognitions
   */
  async getByClassId(classId, options = {}) {
    // Guard: require schoolId for hierarchical collections
    if (!options.schoolId) {
      console.warn('RecognitionRepository.getByClassId: schoolId required but not provided');
      return [];
    }

    const constraints = [
      where('classId', '==', classId)
    ];

    // Pass schoolId for hierarchical routing
    const results = await this.queryDocuments(constraints, options.schoolId);

    // Sort client-side by issuedAt (desc)
    results.sort((a, b) => {
      const timeA = a.issuedAt?.toMillis?.() || 0;
      const timeB = b.issuedAt?.toMillis?.() || 0;
      return timeB - timeA;
    });

    // Apply limit client-side
    if (options.limit) {
      filtered = results.slice(0, options.limit);
    }

    return results;
  }

  /**
   * Get recent recognitions for a school
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @param {number} limitCount - Number of recognitions to fetch
   * @returns {Promise<Array>} Array of recent recognitions
   */
  async getRecent(schoolId, limitCount = 10) {
    // Guard: require schoolId for hierarchical collections
    if (!schoolId) {
      console.warn('RecognitionRepository.getRecent: schoolId required but not provided');
      return [];
    }

    const constraints = [];

    // Pass schoolId for hierarchical routing (no need for where clause)
    const results = await this.queryDocuments(constraints, schoolId);

    // Sort by issuedAt desc and limit client-side
    results.sort((a, b) => {
      const timeA = a.issuedAt?.toMillis?.() || 0;
      const timeB = b.issuedAt?.toMillis?.() || 0;
      return timeB - timeA;
    });

    return results.filter(isIssuedRecognition).slice(0, limitCount);
  }

  /**
   * Get recognitions by value for a period
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @param {string} valueId - Value ID
   * @param {Date} startDate - Period start date
   * @param {Date} endDate - Period end date
   * @returns {Promise<Array>} Array of recognitions
   */
  async getByValueForPeriod(schoolId, valueId, startDate, endDate) {
    // Guard: require schoolId for hierarchical collections
    if (!schoolId) {
      console.warn('RecognitionRepository.getByValueForPeriod: schoolId required but not provided');
      return [];
    }

    const constraints = [
      where('valueId', '==', valueId)
    ];

    // Pass schoolId for hierarchical routing
    const results = await this.queryDocuments(constraints, schoolId);

    // Filter by date range client-side
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();

    const filtered = results.filter(r => {
      const time = r.issuedAt?.toMillis?.() || 0;
      return time >= startTime && time <= endTime;
    });

    return filtered;
  }

  /**
   * Get all recognitions (recognition types, not issued recognitions)
   * @param {string} schoolId - School ID
   * @param {boolean} activeOnly - Only return active recognitions
   * @returns {Promise<Array>} Array of recognitions
   */
  async getRecognitions(schoolId, activeOnly = true) {
    if (!schoolId) {
      console.warn('RecognitionRepository.getRecognitions: schoolId required but not provided');
      return [];
    }

    // Definitions have names. Do not scan the school's issued learner history
    // to populate a configuration catalogue. issuedAt is also on old definitions.
    const snapshot = await getDocs(query(this.getCollectionRef(schoolId), orderBy('name')));
    const results = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })).filter(isRecognitionDefinition);

    // Filter out explicitly inactive recognitions (active=false) if activeOnly
    const filteredResults = activeOnly
      ? results.filter(r => r.active !== false && r.retired !== true)
      : results;

    // Sort by sortOrder if exists, otherwise by name
    return filteredResults.sort((a, b) => {
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
  }

  subscribeDefinitions(callback, schoolId) {
    if (!schoolId) { callback([]); return () => {}; }
    return onSnapshot(query(this.getCollectionRef(schoolId), orderBy('name')), snapshot => {
      callback(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })).filter(isRecognitionDefinition));
    }, error => {
      console.error('Recognition definitions could not be loaded:', error);
      callback([]);
    });
  }

  /**
   * Subscribe to recognitions changes (recognition types, not issued recognitions)
   * @param {Function} callback - Callback for updates
   * @param {Array} constraints - Query constraints
   * @param {string} schoolId - School ID
   * @returns {Function} Unsubscribe function
   */
  subscribeRecognitions(callback, constraints = [], schoolId) {
    if (!schoolId) {
      console.warn('RecognitionRepository.subscribeRecognitions: schoolId required but not provided');
      callback([]);
      return () => {};
    }

    return this.subscribe(callback, constraints, schoolId);
  }

  /**
   * Mark recognition as having triggered badges
   * @param {string} recognitionId - Recognition ID
   * @param {Array<string>} badgeIds - Array of badge IDs that were triggered
   * @returns {Promise<void>}
   */
  async markBadgesTriggered(recognitionId, badgeIds) {
    await super.update(recognitionId, {
      triggeredBadges: badgeIds,
      processedForRules: true
    });
  }

  /**
   * Mark recognition as parent notified
   * @param {string} recognitionId - Recognition ID
   * @returns {Promise<void>}
   */
  async markParentNotified(recognitionId) {
    await super.update(recognitionId, {
      parentNotified: true,
      parentNotifiedAt: serverTimestamp()
    });
  }
}
